import { Grid2, Divider, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import Icon from '@components/Icon/Icon';
import NullStateDiv from '@components/NullStateDiv';

import { useGetStripeInvoiceList } from '@hooks/api/stripe';

import type { ManageAccountModel } from '@models/account';

const messages = defineMessages({
  invoices: {
    id: 'invoices.invoices',
    defaultMessage: 'Invoices',
  },
  invoiceNumber: {
    id: 'invoices.invoiceNumber',
    defaultMessage: 'Invoice Number',
  },
  date: {
    id: 'GLOBAL.date',
    defaultMessage: 'Date',
  },
  download: {
    id: 'invoices.download',
    defaultMessage: 'Download',
  },
  noInvoices: {
    id: 'invoices.noInvoices',
    defaultMessage: 'No Invoices',
  },
});

interface StripeInvoicesProps {
  account?: ManageAccountModel;
  loading: boolean;
}

const Invoices: React.FC<StripeInvoicesProps> = ({
  account,
  loading: baseLoading,
}) => {
  const { formatMessage } = useIntl();

  const { data: stripeInvoicesData, loading: stripeInvoicesLoading } =
    useGetStripeInvoiceList({ account_id: account?.account_id ?? '' });
  const invoices = stripeInvoicesData?.listStripeInvoices ?? [];

  const loading = baseLoading || stripeInvoicesLoading;

  return (
    <ContentLoader loading={loading}>
      {invoices.length > 0 ? (
        <MuiDataTable
          columns={[
            {
              name: 'number',
              options: {
                customHeadRender: () => null,
                customBodyRender: (invoiceNumber: number) => (
                  <Typography variant="body1">{invoiceNumber}</Typography>
                ),
              },
            },
            {
              name: 'created',
              options: {
                customHeadRender: () => null,
              },
            },
            {
              name: 'invoice_pdf',
              options: {
                customHeadRender: () => null,
                customBodyRender: (value: string) => (
                  <a download={formatMessage(messages.download)} href={value}>
                    <Icon
                      color="text.secondary"
                      name="download"
                      size="lg"
                      type="fas"
                    />
                  </a>
                ),
              },
            },
          ]}
          data={invoices.map((invoice) => ({
            ...invoice,
            created: new Date(invoice.created * 1000),
          }))}
          options={{
            sortOrder: {
              direction: 'desc',
              name: 'created',
            },
            customRowRender: (
              data: React.ReactNode[],
              dataIndex: number
              // rowIndex: number
            ) => {
              const [invoiceNumber, date, pdfDownload] = data;
              return (
                <tr
                  key={`stripe-invoices-${dataIndex}`}
                  style={{
                    height: 'auto',
                  }}
                >
                  <td
                    style={{
                      paddingTop: dataIndex === 0 ? 20 : 10,
                    }}
                  >
                    <Grid2 container spacing={3} p={2}>
                      <Grid2 size={{ xs: 10 }}>
                        {invoiceNumber}

                        <Typography color="text.secondary" variant="caption">
                          {date}
                        </Typography>
                      </Grid2>
                      <Grid2 textAlign="right" size={{ xs: 2 }}>
                        {pdfDownload}
                      </Grid2>
                    </Grid2>
                    {(stripeInvoicesData?.listStripeInvoices.length ?? 0) >
                    dataIndex + 1 ? (
                      <Divider />
                    ) : null}
                  </td>
                </tr>
              );
            },
          }}
        />
      ) : (
        <NullStateDiv>{formatMessage(messages.noInvoices)}</NullStateDiv>
      )}
    </ContentLoader>
  );
};

export default Invoices;
